import React from 'react';
import clsx from 'classnames';
import {faCheck, faStar} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import * as colors from '@common/style/color';
import useLocalStorage from '@common/hooks/use-local-storage';
import {ReactComponent as ApolloIcon} from '@gakki/assets/apollo.svg';
import {ReactComponent as FoundationIcon} from '@gakki/assets/person.svg';
import {ReactComponent as PayrollIcon} from '@gakki/assets/money.svg';
import {ReactComponent as AttendanceIcon} from '@gakki/assets/rocket.svg';
import {StepStatus} from '@gakki/context/app-context';

const stepConfig = [
  {
    abbr: 'FD',
    moduleName: '人事組織',
    moduleIcon: <FoundationIcon className="mr-[5px]" />,
    subSteps: [
      {
        step: 1,
        subStepName: '職稱',
      },
      {
        step: 2,
        subStepName: '人員',
      },
      {
        step: 3,
        subStepName: '組織',
      },
    ]
  },
  {
    abbr: 'PT',
    moduleName: '行動出勤',
    moduleIcon: <AttendanceIcon className="mr-[5px]" />,
    subSteps: [
      {
        step: 4,
        subStepName: '上下班/彈性工時',
      },
      {
        step: 5,
        subStepName: '剩餘假別',
      },
      {
        step: 6,
        subStepName: '打卡方式',
      },
      {
        step: 7,
        subStepName: '排班',
      },
    ]
  },
  {
    abbr: 'PY',
    moduleName: '智慧結薪',
    moduleIcon: <PayrollIcon className="mr-[5px]" />,
    subSteps: [
      {
        step: 8,
        subStepName: '發薪',
      },
      {
        step: 9,
        subStepName: '公司設定',
      },
      {
        step: 10,
        subStepName: '員工設定',
      },
    ]
  },
  {
    abbr: 'XE',
    moduleName: 'Apollo XE',
    moduleIcon: <ApolloIcon className="mr-[5px]" />,
    subSteps: [
      {
        step: 11,
        subStepName: '管理者',
      },
      {
        step: 12,
        subStepName: '啟用',
      },
    ]
  },
]

const StepModule = (props: {disabled?: boolean, children?: React.ReactNode}) => (
  <div
    className={clsx(`flex items-center pr-5 min-h-[33px] cursor-default select-none 
    rounded-[100px] text-brand-black border border-solid border-brand-black`, {
      ['!border-brand-disabled']: props.disabled,
      ['!text-brand-disabled']: props.disabled,
    })}
    {...props}
  />
);

const StepDivider = (props: any) => (
  <div
    className="w-2.5 h-0.5 bg-brand-gray my-0 mx-2"
    {...props}
  />
);

type SubStepProps = {
  step: number;
  subStepName: string;
  checked?: boolean;
  disabled?: boolean;
  active?: boolean;
  onStepChange: (step: number) => void;
  changeAble?: boolean;
};

function SubStep(props: SubStepProps) {
  const {
    checked,
    disabled,
    subStepName,
    active,
    step,
    onStepChange,
  } = props;

  return (
    <button
      className={clsx(`bg-transparent border-none outline-none text-brand-black flex items-center justify-between hover:text-brand-black`, {
        [`!text-brand-disabled`]: disabled && !checked && !active,
        [`hover:!text-brand-disabled`]: disabled && !checked && !active,
        [`!text-brand`]: active,
      })}
      disabled={disabled}
      onClick={() => onStepChange(step)}
    >
      <FontAwesomeIcon
        className="mr-[5px]"
        icon={checked ? faCheck : faStar}
      />
      <span>{subStepName}</span>
    </button>
  );
}

type StepProps = {
  moduleName: string;
  moduleIcon: React.ReactNode;
  subSteps: Omit<
    SubStepProps,
    'checked' | 'disabled' | 'active' | 'onStepChange' | 'changeAble'
  >[];
  currentStep: number;
  progressStep?: number;
  onStepChange: (step: number) => void;
  changeAble?: boolean;
};

function Step(props: StepProps) {
  const {
    moduleIcon,
    moduleName,
    subSteps,
    currentStep,
    progressStep,
    onStepChange,
    changeAble,
  } = props;
  const minStep = subSteps.reduce((min, s) => {
    return min > s.step ? s.step : min;
  }, Number.MAX_VALUE);

  return (
    <>
      <StepModule disabled={(progressStep || currentStep) < minStep}>
        {moduleIcon}
        {moduleName}
      </StepModule>
      <StepDivider />
      {subSteps.map((s, idx) => (
        <React.Fragment key={`Step_${idx}`}>
          <SubStep
            {...s}
            onStepChange={onStepChange}
            checked={s.step < (progressStep || currentStep)}
            disabled={s.step > (progressStep || currentStep) || !changeAble}
            active={s.step === currentStep}
          />
          {idx !== subSteps.length - 1 && <StepDivider />}
        </React.Fragment>
      ))}
    </>
  );
}

type ImportStepProps = {
  progressStep?: number;
  onStepChange: (step: number) => void;
  enablePY: boolean;
}

export default function ImportStep(props: ImportStepProps) {
  const {progressStep, onStepChange, enablePY} = props
  const [stepStatus] = useLocalStorage<StepStatus>('stepStatus', StepStatus.init);
  const [step] = useLocalStorage<number>('step', 0);
  const changeAble = stepStatus !== StepStatus.active;

  return (
    <div className="step flex items-center flex-row p-[15px] bg-white h-[62px]">
      {
        stepConfig.map((stepProps, idx) => {
          if (stepProps.abbr === 'PY' && !enablePY) {
            return null;
          }
          const notLast = idx !== stepConfig.length - 1
          return (
            <React.Fragment key={`step_${idx}`}>
              <Step
                {...stepProps}
                changeAble={changeAble}
                currentStep={step}
                progressStep={stepStatus === StepStatus.init || stepStatus === StepStatus.updated ? (progressStep || step) : step}
                onStepChange={onStepChange}
              />
              {notLast && <StepDivider />}
            </React.Fragment>
          )
        }).filter(s=>s)
      }
    </div>
  );
}
